export class ApiError extends Error {
  errorMessage: string
  statusCode: number | undefined
  response: Response | undefined
  constructor(errorMessage: string, statusCode?: number, response?: Response) {
    super()
    this.errorMessage = errorMessage
    this.statusCode = statusCode
    this.response = response
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleApiError = (url: string, e: any, verbose = false): ApiError => {
  let apiError: ApiError
  
  if (e.name === 'ResponseError') {
    const statusCode = e.response.status
    if (statusCode === 401 || statusCode === 403) {
      apiError = new ApiError(
        'Access token is not valid or does not have custom component permissions.',
        statusCode,
        e.response,
      )
    } else if (statusCode >= 500) {
      apiError = new ApiError(
        `An unexpected error was returned by ${url}: status code: ${statusCode}, error: ${e.response.statusText}.`,
        statusCode,
        e.response,
      )
    } else if (statusCode === 409) {
      apiError = new ApiError(
        'The custom component library is being updated by someone else at this time.',
        statusCode,
        e.response,
      )
    } else if (statusCode === 429) {
      apiError = new ApiError(`Too many requests to ${url}. Please try again later.`, statusCode, e.response)
    } else {
      apiError = new ApiError(
        `An unknown error was returned by ${url}: status code: ${statusCode}, error: ${e.response.statusText}.`,
        statusCode,
        e.response,
      )
    }
  } else if (e.name === 'FetchError') {
    const causeCode = e.cause.cause && (e.cause.cause as { code: string }).code

    if (causeCode === 'ENOTFOUND') {
      apiError = new ApiError(`Could not connect to given url, as the domain name did not resolve: ${url}.`)
    } else if (causeCode === 'ERR_SSL_WRONG_VERSION_NUMBER') {
      apiError = new ApiError(
        `There was an error with TLS. Check the protocol in your url is correct (http vs https). The error was: ${JSON.stringify(
          e,
          null,
          2,
        )}.`,
      )
    } else if (causeCode === 'ECONNREFUSED') {
      apiError = new ApiError(`The connection was refused. The error was: ${JSON.stringify(e, null, 2)}.`)
    } else {
      apiError = new ApiError(`Failed to connect to the url. The error was: ${JSON.stringify(e, null, 2)}.`)
    }
  } else {
    apiError = new ApiError(`An unknown error occurred. The error was: ${JSON.stringify(e, null, 2)}.`)
  }

  // Add verbose logging if enabled
  if (verbose) {
    console.error('\n--- Verbose API Error Details ---')
    console.error('Request URL:', url)
    console.error('Original Error Name:', e.name)
    console.error('Original Error:', e)
    
    if (e.response) {
      console.error('Response Status:', e.response.status)
      console.error('Response Status Text:', e.response.statusText)
      console.error('Response URL:', e.response.url)
      console.error('Response Headers:', Object.fromEntries(e.response.headers.entries()))
      
      // Try to get response body
      try {
        const responseClone = e.response.clone()
        responseClone.text().then((responseText: string) => {
          console.error('Response Body:', responseText)
          
          // Try to parse as JSON for better formatting
          try {
            const jsonBody = JSON.parse(responseText)
            console.error('Response Body (JSON):', JSON.stringify(jsonBody, null, 2))
          } catch {
            // Already logged as text above
          }
        }).catch((bodyError: any) => {
          console.error('Could not read response body:', bodyError)
        })
      } catch (cloneError) {
        console.error('Could not clone response for body reading:', cloneError)
      }
    }
    
    if (e.cause) {
      console.error('Error Cause:', e.cause)
    }
    
    console.error('Generated ApiError Message:', apiError.errorMessage)
    console.error('Generated ApiError Status Code:', apiError.statusCode)
    console.error('--- End Verbose API Error Details ---\n')
  }

  return apiError
}
